import EmblaCarousel from 'embla-carousel';
import {
    ANNUAL,
    EVERY_30_DAYS,
    getFilteredSortedPlans,
    getIntervalShortLabel,
    getPlanButtonText,
    formatFeature,
    isPlanButtonDisabled,
    translateMe,
    deepEqual,
    emitEvent, isPlanWithNote,
} from '@/helpers/helper.js';

/**
 * PlanCardSlider custom element
 * @property {Array} plans - List of plan objects to display
 * @property {Array} features - List of feature objects
 * @property {Object} currentPlan - The current plan object
 * @property {Boolean} hasActiveCharge - Whether the user has an active charge
 * @property {String} shopifyPlan - The current Shopify plan
 * @property {Object} promotionalDiscount - Promotional discount object
 * @property {String} selectedInterval - Selected interval (monthly/annual)
 */
class PlanCardSlider extends HTMLElement {
    static get observedAttributes() {
        return [
            'has-active-charge',
            'shopify-plan',
            'selected-interval',
        ];
    }

    constructor() {
        super();
        this.attachShadow({mode: 'open'});

        // external libraries
        this._embla = null;

        // data
        this._plans = [];
        this._features = [];
        this._currentPlan = {};
        this._hasActiveCharge = false;
        this._shopifyPlan = '';
        this._selectedInterval = EVERY_30_DAYS;
        this._promotionalDiscount = null;

        this.anyMonthlyPlanHasDiscount = false;
        this.anyMonthlyPlanHasNote = false;
        this.anyAnnualPlanHasDiscount = false;
        this.anyAnnualPlanHasNote = false;

        // UI state
        this._initialized = false;
        this._loadingPlanId = null;

        // observers
        this._resizeObserver = null;
        this._featureResizeObserver = null;
        this._headerResizeObserver = null;
        this._dotListeners = [];
    }

    connectedCallback() {
        if (!this._initialized) {
            this._initialized = true;
            this._renderShell();
        }
        if (!this._resizeObserver) {
            this._resizeObserver = new ResizeObserver(() => {
                this._embla?.reInit();
                this._setDynamicSlideWidths();
            });
            this._resizeObserver.observe(this);
        }
        this._render();
    }

    disconnectedCallback() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case 'has-active-charge':
                this.hasActiveCharge = newValue === 'true';
                break;
            case 'shopify-plan':
                this.shopifyPlan = newValue;
                break;
            case 'selected-interval':
                this.selectedInterval = newValue;
                break;
        }
        this._render();
    }


    // --- Property Getters/Setters ---
    get plans() { return this._plans; }
    set plans(value) {
        if (!deepEqual(this._plans, value)) {
            this._plans = Array.isArray(value) ? value : [];
            this._render();
        }
    }

    get features() { return this._features; }
    set features(value) {
        if (!deepEqual(this._features, value)) {
            this._features = Array.isArray(value) ? value : [];
            this._render();
        }
    }

    get currentPlan() { return this._currentPlan; }
    set currentPlan(value) {
        if (!deepEqual(this._currentPlan, value)) {
            this._currentPlan = value || {};
            this._render();
        }
    }

    get hasActiveCharge() { return this._hasActiveCharge; }
    set hasActiveCharge(value) {
        if (this._hasActiveCharge !== !!value) {
            this._hasActiveCharge = !!value;
            this._render();
        }
    }

    get shopifyPlan() { return this._shopifyPlan; }
    set shopifyPlan(value) {
        if (this._shopifyPlan !== value) {
            this._shopifyPlan = value;
            this._render();
        }
    }

    get promotionalDiscount() { return this._promotionalDiscount; }
    set promotionalDiscount(value) {
        if (!deepEqual(this._promotionalDiscount, value)) {
            this._promotionalDiscount = value;
            this._render();
        }
    }

    get selectedInterval() { return this._selectedInterval; }
    set selectedInterval(value) {
        if (this._selectedInterval !== value) {
            this._selectedInterval = value;
            this._render();
        }
    }


    // --- Internal Helpers ---
    _getDiscountFlag() {
        return this._selectedInterval === ANNUAL ? this.anyAnnualPlanHasDiscount : this.anyMonthlyPlanHasDiscount;
    }

    _getNoteFlag() {
        return false; // temporarily disable note across all plans as per new design, but keep the logic in place for future use
        // return this._selectedInterval === ANNUAL ? this.anyAnnualPlanHasNote : this.anyMonthlyPlanHasNote;
    }

    _getPlansByInterval(interval) {
        try {
            let filterData = getFilteredSortedPlans(
                Array.isArray(this._plans) ? this._plans : [],
                interval,
                this.promotionalDiscount,
                this.shopifyPlan,
                this.currentPlan,
                this.hasActiveCharge
            );
            if (this._selectedInterval === ANNUAL) {
                this.anyAnnualPlanHasDiscount = filterData.anyPlanHasDiscount;
                this.anyAnnualPlanHasNote = filterData.anyPlanHasNote;
            } else {
                this.anyMonthlyPlanHasDiscount = filterData.anyPlanHasDiscount;
                this.anyMonthlyPlanHasNote = filterData.anyPlanHasNote;
            }
            return Array.isArray(filterData.filteredPlans) ? filterData.filteredPlans : [];
        } catch (e) {
            console.error('Error in _getPlansByInterval:', e, this._plans);
            return [];
        }
    }

    _getSlidesPerView() {
        const width = window.innerWidth;
        let slidesPerView = 1;
        if (width >= 1440) {
            slidesPerView = 4;
        } else if (width >= 992) {
            slidesPerView = 3;
        } else if (width >= 640) {
            slidesPerView = 2;
        }
        return Math.min(slidesPerView, this._plans.length || 2);
    }

    _getSortedPlanFeatures(plan) {
        try {
            const assignedFeatures = plan.features ? Object.keys(plan.features) : [];
            return this._features
                .filter(feature => assignedFeatures.includes(feature.uuid) && !feature.hidden_feature)
                .sort((a, b) => {
                    const popularityA = parseInt(a.popularity) || 999;
                    const popularityB = parseInt(b.popularity) || 999;
                    if (popularityA !== popularityB) return popularityA - popularityB;
                    return (parseInt(a.display_order) || 999) - (parseInt(b.display_order) || 999);
                })
                .slice(0, 4)
                .map(feature => ({
                    ...feature,
                    value: plan.features[feature.uuid]?.value || plan.features[feature.uuid]
                }));
        } catch (e) {
            console.error('Error in _getSortedPlanFeatures:', e, plan);
            return [];
        }
    }


    // --- Render Methods ---
    _renderShell() {
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display:block;
                }
                .app-manager__width-container,
                .app-manager__width-container *,
                .app-manager__width-container *::before,
                .app-manager__width-container *::after {
                    box-sizing: border-box;
                }
                .plan-cards-slider {
                    position:relative;
                    padding-top: 8px;
                    padding-bottom: 8px;
                    margin-left:auto;
                    margin-right:auto;
                }
                .app-manager__width-container.plan-cards-slider {
                    padding-left: 96px;
                    padding-right: 96px;
                    max-width: calc(1200px + 8px);
                }
                .plan-cards-slider__embla {
                    overflow:hidden;
                    width:100%;
                }
                .plan-cards-slider__container {
                    display:flex;
                    gap:0;
                    align-items: stretch;
                }
                .plan-cards-slider__slide {
                    flex:0 0 25%;
                    min-width:0;
                }
                .plan-card {
                    display: flex;
                    flex-direction: column;
                    width: calc(100% - 8px);
                    height: 100%;
                    margin: auto;
                    padding: 16px 24px;
                    position: relative;
                    z-index: 0;
                    box-shadow: 0 4px 6px -1px #0000001a;
                    background-color: #fff;
                    border: 1px solid #e5e5e5;
                    border-radius: 8px;
                    box-sizing: border-box;
                }
                .plan-card__section {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .plan-card__row {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .plan-card__muted {
                    color: #616161;
                }
                .plan-card__heading {
                    color: #303030;
                    font-size: 16px;
                    line-height: 20px;
                    font-weight: bold;
                    margin: 0;
                    text-align: center;
                }
                .plan-card__price-section {
                    min-height: 30px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    box-sizing: border-box;
                }
                .plan-card__price-section--any-plan-has-discount {
                    min-height: 64px;
                }
                .plan-card__strike-price-row {
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    align-items: baseline;
                }
                .plan-card__strike-price {
                    font-size: 16px;
                    line-height: 20px;
                    font-weight: 300;
                    margin: 0;
                    text-align: center;
                    text-decoration: line-through;
                    color: #303030;
                }
                .plan-card__price-row {
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    align-items: baseline;
                }
                .plan-card__price {
                    font-size: 30px;
                    line-height: 36px;
                    margin: 0;
                    text-align: center;
                    color: #303030;
                }
                .plan-card__strike-price-row .plan-card__plan-interval,
                .plan-card__price-row .plan-card__plan-interval {
                    font-size: 13px;
                }
                .plan-card__centered-row {
                    display: flex;
                    gap: 8px;
                    justify-content: center;
                    align-items: center;
                }
                .plan-card__description-text {
                    margin-top: 4px;
                    margin-bottom: 4px;
                    font-size: 13px;
                    line-height: 20px;
                    text-align: center;
                }
                .plan-card__trial-text {
                    margin-top: 4px;
                    margin-bottom: 4px;
                    font-size: 13px;
                    line-height: 20px;
                }
                .plan-card__feature-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }
                .plan-card__feature {
                    margin: 0 0 4px 0;
                    font-size: 13px;
                    line-height: 20px;
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                    gap: 4px;
                    font-weight: 400;
                }
                .plan-card__feature-icon {
                    height: 20px;
                }
                .plan-card__note {
                    margin-top: 4px;
                    margin-bottom: 4px;
                    font-size: 13px;
                    line-height: 20px;
                }
                .plan-cards-slider__controls {
                    display:flex;
                    justify-content:space-between;
                    gap:12px;
                    margin-top:16px;
                    width:calc(100% - 64px);
                    position:absolute;
                    left:50%;
                    top:calc(50% - 24px);
                    transform:translate(-50%, -50%);
                }
                .plan-cards-slider__arrow-group {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: relative;
                }
                .plan-cards-slider__arrow-count {
                    position: absolute;
                    top: -44px;
                    background-color: #fff;
                    color: #333;
                    font-weight: 500;
                    padding: 6px 12px;
                    border-radius: 8px;
                    font-size: 14px;
                    line-height: 20px;
                    white-space: nowrap;
                    box-shadow: 0 4px 8px #00000026;
                }
                .plan-cards-slider__arrow-count::after {
                    content: '';
                    position: absolute;
                    left: 50%;
                    top: 100%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 8px solid transparent;
                    border-right: 8px solid transparent;
                    border-top: 8px solid #fff;
                    filter: drop-shadow(0 1px 2px #0001);
                }
                .plan-cards-slider__arrow-count::before {
                    content: '';
                    position: absolute;
                    left: 50%;
                    top: 100%;
                    transform: translateX(-50%);
                    width: 0;
                    height: 0;
                    border-left: 9px solid transparent;
                    border-right: 9px solid transparent;
                    border-top: 9px solid #E3E3E3;
                    z-index: -1;
                }
                .plan-cards-slider__dots {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    margin-top: 16px;
                }
                .plan-cards-slider__dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #ccc;
                    cursor: pointer;
                }
                .plan-cards-slider__dot--active {
                    background: #0094d5;
                    width: 12px;
                    height: 12px;
                }
                .is-visible {
                    visibility: visible;
                }
                .is-hidden {
                    visibility: hidden;
                }
                @media screen and (max-width: 1200px) {
                    .app-manager__width-container.plan-cards-slider {
                        max-width: calc(960px + 8px);
                    }
                    .plan-cards-slider__slide {
                        flex: 0 0 33.3333%;
                    }
                }
                @media screen and (max-width: 900px) {
                    .app-manager__width-container.plan-cards-slider {
                        max-width: calc(760px + 8px);
                    }
                    .plan-cards-slider__slide {
                        flex: 0 0 50%;
                    }
                }
                @media screen and (max-width: 768px) {
                    .app-manager__width-container.plan-cards-slider {
                        max-width: calc(100% + 8px);
                        padding-left: 0;
                        padding-right: 0;
                        margin-left: -4px;
                        margin-right: -4px;
                    }
                    .plan-cards-slider__controls {
                        display: none; !important;
                    }
                }
                @media screen and (max-width: 540px) {
                    .plan-cards-slider__slide {
                        flex: 0 0 100%;
                    }
                }
            </style>
            <div class="plan-cards-slider app-manager__width-container">
                <div class="plan-cards-slider__embla" aria-label="Plan carousel" tabindex="0">
                    <div class="plan-cards-slider__container"></div>
                </div>
                <div class="plan-cards-slider__controls">
                    <span class="plan-cards-slider__arrow-group">
                        <span id="left-tooltip" class="plan-cards-slider__arrow-count"></span>
                        <app-button class="plan-cards-slider__arrow plan-cards-slider__arrow--prev" variant="primary" icon-only aria-label="Previous plan" tabindex="0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_859_9388)"><path d="M5 12.0001L19 12.0001" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M5 12.0001L11 18.0001" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M5 12.0001L11 6.00012" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></g> <defs><clipPath id="clip0_859_9388"><rect width="24" height="24" fill="white"></rect></clipPath></defs></svg>
                        </app-button>
                    </span>
                    <span class="plan-cards-slider__arrow-group">
                        <span id="right-tooltip" class="plan-cards-slider__arrow-count"></span>
                        <app-button class="plan-cards-slider__arrow plan-cards-slider__arrow--next" variant="primary" icon-only aria-label="Next plan" tabindex="0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_859_9381)"><path d="M19 12H5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M19 12L13 6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M19 12L13 18" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></g> <defs><clipPath data-v-0cb6e3a5="" id="clip0_859_9381"><rect width="24" height="24" fill="white" transform="matrix(-1 0 0 -1 24 24)"></rect></clipPath></defs></svg>
                        </app-button>
                    </span>
                </div>
                <div class="plan-cards-slider__dots" aria-label="Carousel navigation"></div>
            </div>
        `;
        this._container = this.shadowRoot.querySelector('.plan-cards-slider__container');
    }

    _render() {
        try {
            const interval = this._selectedInterval === ANNUAL ? ANNUAL : EVERY_30_DAYS;
            const plans = this._getPlansByInterval(interval);
            const container = this.shadowRoot.querySelector('.plan-cards-slider__container');
            if (!container) {
                return;
            }
            this._removeDotListeners();
            this._dotListeners = [];
            container.innerHTML = '';
            plans.forEach((plan) => {
                const features = this._getSortedPlanFeatures(plan);
                const cardHTML = this._renderPlanCard(plan, features);
                const slide = document.createElement('div');
                slide.className = 'plan-cards-slider__slide';
                slide.innerHTML = cardHTML;
                // Handle app-button
                const appButton = slide.querySelector('app-button.plan-card__button');
                if (appButton) {
                    appButton.addEventListener('app-button-click', () => {
                        this._loadingPlanId = plan.id;
                        this._render();
                        emitEvent(this, 'app-manager:plan-click', { plan });
                        setTimeout(() => {
                            this._loadingPlanId = null;
                            this._render();
                        }, 2000);
                    });
                }
                container.appendChild(slide);
            });
            this._setDynamicSlideWidths();
            if (plans.length > 0) {
                this._initCarousel();
            } else if (this._embla) {
                this._embla.destroy();
                this._embla = null;
            }
        } catch (e) {
            console.error('Error in PlanCardSlider._render:', e);
        }
    }

    _renderPlanCard(plan, features) {
        try {
            const planId = plan.id;
            const name = translateMe(plan.name || '');
            const description = translateMe(plan.description || '');
            let price = Number(plan.price);
            let strikePrice = Number(plan.strike_price);
            if (isNaN(price)) {
                console.error('Invalid plan.price for plan', plan);
                price = 0;
            }
            if (plan.has_discount && isNaN(strikePrice)) {
                console.error('Invalid plan.strike_price for plan', plan);
                strikePrice = null;
            }
            const trialDays = Number(plan.trial_days) || 0;
            const interval = plan.interval || EVERY_30_DAYS;
            const hasActiveCharge = this._hasActiveCharge;
            const buttonDisabled = isPlanButtonDisabled(this._shopifyPlan, plan, hasActiveCharge, this._currentPlan) || !!this._loadingPlanId;
            const isThisPlanLoading = this._loadingPlanId === planId;
            const anyPlanHasDiscount = this._getDiscountFlag();
            const anyPlanHasNote = this._getNoteFlag();
            const isThisPlanHasNote = isPlanWithNote(this._shopifyPlan, plan, this._currentPlan, hasActiveCharge);
            const buttonContent = getPlanButtonText(this._shopifyPlan, plan, this._currentPlan, false, hasActiveCharge);
            return `
                <div class="plan-card">
                    <div class="plan-card__section">
                        <div class="plan-card__row">
                            <h3 class="plan-card__heading">${name}</h3>
                            <div class="plan-card__price-section ${anyPlanHasDiscount ? 'plan-card__price-section--any-plan-has-discount' : ''}">
                                <div class="plan-card__strike-price-row">
                                    ${strikePrice ? `
                                        <h5 class="plan-card__strike-price">$${strikePrice.toFixed(2)}</h5>
                                        <span class="plan-card__plan-interval plan-card__muted">/${getIntervalShortLabel(interval)}</span>
                                    ` : ''}
                                </div>
                                <div class="plan-card__price-row">
                                    <h2 class="plan-card__price">${price > 0 ? '$' + price.toFixed(2) : translateMe('Free')}</h2>
                                    <span class="plan-card__plan-interval plan-card__muted">${price !== 0 ? `/<span class="plan-card__muted">${getIntervalShortLabel(interval)}</span>` : ''}</span>
                                </div>
                            </div>
                        </div>
                        <div class="plan-card__row">
                            <div class="plan-card__centered-row">
                                <div class="plan-card__description-wrapper" style="visibility:${description && description !== 'null' ? 'visible' : 'hidden'}">
                                    <p class="plan-card__description-text plan-card__muted">${description && description !== 'null' ? description : 'Description'}</p>
                                </div>
                            </div>
                            <div class="plan-card__centered-row">
                                <app-button class="plan-card__button" variant="secondary" inline-size="fill" aria-label="Choose Plan" tabindex="0" ${buttonDisabled ? 'disabled' : ''} ${isThisPlanLoading ? 'loading' : ''}>
                                    ${buttonContent}
                                </app-button>
                            </div>
                            <div class="plan-card__centered-row">
                                <div class="plan-card__trial-wrapper" style="visibility:${trialDays > 0 ? 'visible' : 'hidden'}">
                                    <p class="plan-card__trial-text plan-card__muted">${trialDays + translateMe('-day free trial')}</p>
                                </div>
                            </div>
                        </div>
                        <div class="plan-card__row">
                            <ul class="plan-card__feature-list">
                                ${features.map(feature => `
                                    <li class="plan-card__feature">
                                        <span class="plan-card__feature-icon" aria-hidden="true">
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path fill-rule="evenodd" clip-rule="evenodd" d="M15.7803 5.96967C16.0732 6.26256 16.0732 6.73744 15.7803 7.03033L9.28033 13.5303C8.98744 13.8232 8.51256 13.8232 8.21967 13.5303L4.96967 10.2803C4.67678 9.98744 4.67678 9.51256 4.96967 9.21967C5.26256 8.92678 5.73744 8.92678 6.03033 9.21967L8.75 11.9393L14.7197 5.96967C15.0126 5.67678 15.4874 5.67678 15.7803 5.96967Z" fill="#1A1A1A"/>
                                            </svg>
                                        </span>
                                        <span class="plan-card__muted">
                                            ${feature.value_type !== 'boolean' ? translateMe(formatFeature(feature, null)) : ''}
                                            ${translateMe(feature.name)}
                                        </span>
                                    </li>
                                `).join('')}
                            </ul>
                        </div>
                        ${anyPlanHasNote ? `
                            <div class="plan-card__centered-row">  
                                <div class="plan-card__note-wrapper" style="visibility:${isThisPlanHasNote ? 'visible' : 'hidden'}">     
                                    <p class="plan-card__note plan-card__muted">${translateMe('Note: On account of your recent Shopify plan upgrade, you should consider upgrading your current app plan')}</p>
                                </div>
                            </div>` : ''
                        }
                    </div>
                </div>
            `;
        } catch (e) {
            console.error('Error in _renderPlanCard:', e, plan);
            return '<div class="plan-card plan-card--error">Error rendering plan card</div>';
        }
    }


    // --- DOM Update Helpers ---
    _setDynamicSlideWidths() {
        const slides = this.shadowRoot.querySelectorAll('.plan-cards-slider__slide');
        const plansCount = this._plans.length;
        const slidesPerView = this._getSlidesPerView();
        let widthPercent;
        if (plansCount === 0) return;
        if (plansCount < slidesPerView) {
            widthPercent = 100 / plansCount;
        } else {
            widthPercent = 100 / slidesPerView;
        }
        slides.forEach(slide => {
            slide.style.flex = `0 0 ${widthPercent}%`;
        });
    }

    _updateArrowVisibilityAndTooltips() {
        if (!this._embla) return;
        const prevBtn = this.shadowRoot.querySelector('.plan-cards-slider__arrow--prev');
        const nextBtn = this.shadowRoot.querySelector('.plan-cards-slider__arrow--next');
        const prevTooltip = this.shadowRoot.querySelector('#left-tooltip');
        const nextTooltip = this.shadowRoot.querySelector('#right-tooltip');
        const slidesPerView = this._getSlidesPerView();
        const total = this._plans.length;
        const selected = this._embla.selectedScrollSnap();
        const canScrollPrev = this._embla.canScrollPrev();
        const canScrollNext = this._embla.canScrollNext();
        const leftCount = selected;
        const rightCount = total - (selected + slidesPerView);
        prevBtn.style.display = canScrollPrev ? '' : 'none';
        nextBtn.style.display = canScrollNext ? '' : 'none';
        if (prevTooltip) {
            prevTooltip.textContent = leftCount > 0 ? `+${leftCount} plan${leftCount === 1 ? '' : 's'}` : '';
            prevTooltip.style.display = canScrollPrev && leftCount > 0 ? '' : 'none';
        }
        if (nextTooltip) {
            nextTooltip.textContent = rightCount > 0 ? `+${rightCount} plan${rightCount === 1 ? '' : 's'}` : '';
            nextTooltip.style.display = canScrollNext && rightCount > 0 ? '' : 'none';
        }
    }

    _removeDotListeners() {
        this._dotListeners.forEach(({dot, handler}) => {
            dot.removeEventListener('click', handler);
        });
        this._dotListeners = [];
    }


    // --- External Library/Integration ---
    _initCarousel() {
        const viewport = this.shadowRoot.querySelector('.plan-cards-slider__embla');
        if (!viewport || !this._plans.length) return;
        if (this._embla) this._embla.destroy();
        this._embla = EmblaCarousel(viewport, {
            align: 'start',
            loop: false,
            dragFree: false,
        });
        this._initControls();
        this._createDots();
        this._embla.on('select', () => this._updateArrowVisibilityAndTooltips());
        this._embla.on('reInit', () => this._updateArrowVisibilityAndTooltips());
        this._updateArrowVisibilityAndTooltips();
    }

    _initControls() {
        const prev = this.shadowRoot.querySelector('.plan-cards-slider__arrow--prev');
        const next = this.shadowRoot.querySelector('.plan-cards-slider__arrow--next');
        if (!prev || !next) return;
        if (this._onPrevClick && prev.removeEventListener) prev.removeEventListener('click', this._onPrevClick);
        if (this._onNextClick && next.removeEventListener) next.removeEventListener('click', this._onNextClick);
        this._onPrevClick = () => this._embla && this._embla.scrollPrev();
        this._onNextClick = () => this._embla && this._embla.scrollNext();
        prev.addEventListener('click', this._onPrevClick);
        next.addEventListener('click', this._onNextClick);
        if (this._embla && this._embla.on) {
            if (this._onEmblaReInit) this._embla.off && this._embla.off('reInit', this._onEmblaReInit);
            this._onEmblaReInit = () => {
                this._createDots();
            };
            this._embla.on('reInit', this._onEmblaReInit);
        }
    }

    _createDots() {
        const dotsContainer = this.shadowRoot.querySelector('.plan-cards-slider__dots');
        if (!dotsContainer || !this._embla) return;
        dotsContainer.innerHTML = '';
        const scrollSnaps = this._embla.scrollSnapList();
        scrollSnaps.forEach((_, index) => {
            const dot = document.createElement('div');
            dot.className = 'plan-cards-slider__dot';
            const handler = () => {
                this._embla && this._embla.scrollTo(index);
            };
            dot.addEventListener('click', handler);
            this._dotListeners.push({dot, handler});
            dotsContainer.appendChild(dot);
        });
        const updateDots = () => {
            if (!this._embla) return;
            const selected = this._embla.selectedScrollSnap();
            dotsContainer.querySelectorAll('.plan-cards-slider__dot').forEach((dot, i) => {
                dot.classList.toggle('plan-cards-slider__dot--active', i === selected);
            });
        };
        this._embla.on && this._embla.on('select', updateDots);
        updateDots();
    }
}

if (!customElements.get('plan-card-slider')) {
    customElements.define('plan-card-slider', PlanCardSlider);
}

export {PlanCardSlider};
